import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { Gift, Edit2, Check, X, Search, Plus, ExternalLink, RefreshCw, AlertCircle, Trash2, Upload, Loader2 } from 'lucide-react';

const ChristmasBikesAdmin = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [message, setMessage] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const [newBike, setNewBike] = useState({
    codigo: '',
    nombre: '',
    categoria: '12',
    descripcion: '',
    modelo: '',
    precio: '',
    imagen_url: '',
    disponible: true
  });

  const fetchBikes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bikes_navidad_2025')
        .select('*')
        .order('categoria', { ascending: true });

      if (error) throw error;
      setBikes((data || []).map(b => ({
        ...b,
        descripcion: (b.descripcion || '').replace(/bicicentro/gi, 'PROOMTB')
      })));
    } catch (err) {
      console.error('Error fetching christmas bikes:', err);
      setMessage({ type: 'error', text: 'Error al cargar las bicicletas: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBikes();
  }, []);

  const handleStartEdit = (bike) => {
    setEditingId(bike.id);
    setEditPrice(bike.precio.toString());
  };

  const handleSavePrice = async (bikeId) => {
    const numPrice = Number(editPrice);
    if (isNaN(numPrice) || numPrice <= 0) {
      setMessage({ type: 'error', text: 'Por favor ingresa un precio válido mayor a 0.' });
      return;
    }

    try {
      const { error } = await supabase
        .from('bikes_navidad_2025')
        .update({ precio: numPrice })
        .eq('id', bikeId);

      if (error) throw error;

      setBikes(prev => prev.map(b => b.id === bikeId ? { ...b, precio: numPrice } : b));
      setEditingId(null);
      setMessage({ type: 'success', text: '¡Precio actualizado exitosamente!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error updating price:', err);
      setMessage({ type: 'error', text: 'Error al actualizar precio: ' + err.message });
    }
  };

  const handleToggleDisponible = async (bike) => {
    const nextStatus = !bike.disponible;
    try {
      const { error } = await supabase
        .from('bikes_navidad_2025')
        .update({ disponible: nextStatus })
        .eq('id', bike.id);

      if (error) throw error;

      setBikes(prev => prev.map(b => b.id === bike.id ? { ...b, disponible: nextStatus } : b));
      setMessage({ type: 'success', text: `Estado cambiado a ${nextStatus ? 'Disponible' : 'Agotado'}` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error changing availability:', err);
      setMessage({ type: 'error', text: 'Error al cambiar estado: ' + err.message });
    }
  };

  const handleDeleteBike = async (bikeId, bikeNombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${bikeNombre}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('bikes_navidad_2025')
        .delete()
        .eq('id', bikeId);

      if (error) throw error;

      setBikes(prev => prev.filter(b => b.id !== bikeId));
      setMessage({ type: 'success', text: `Bicicleta "${bikeNombre}" eliminada.` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting bike:', err);
      setMessage({ type: 'error', text: 'Error al eliminar: ' + err.message });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La imagen excede el límite de 5MB.' });
      return;
    }

    setUploadingImage(true);
    try {
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `navidad/${Date.now()}_${sanitizedName}`;

      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      const { data: pubData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setNewBike(prev => ({ ...prev, imagen_url: pubData.publicUrl }));
      setMessage({ type: 'success', text: 'Imagen cargada exitosamente.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error uploading image:', err);
      setMessage({ type: 'error', text: 'Error al subir la imagen: ' + err.message });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateBike = async (e) => {
    e.preventDefault();
    if (!newBike.nombre || !newBike.precio) {
      setMessage({ type: 'error', text: 'Nombre y Precio son obligatorios.' });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('bikes_navidad_2025')
        .insert([{
          ...newBike,
          precio: Number(newBike.precio)
        }])
        .select();

      if (error) throw error;

      setBikes(prev => [...prev, ...data]);
      setShowAddModal(false);
      setNewBike({
        codigo: '',
        nombre: '',
        categoria: '12',
        descripcion: '',
        modelo: '',
        precio: '',
        imagen_url: '',
        disponible: true
      });
      setMessage({ type: 'success', text: '¡Bicicleta navideña creada con éxito!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error creating bike:', err);
      setMessage({ type: 'error', text: 'Error al guardar: ' + err.message });
    }
  };

  const filteredBikes = bikes.filter(b => {
    const q = search.toLowerCase();
    return (
      (b.nombre || '').toLowerCase().includes(q) ||
      (b.codigo || '').toLowerCase().includes(q) ||
      (b.categoria || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      
      {/* Title & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-6">
        <div>
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-black uppercase mb-2">
            <Gift className="w-3.5 h-3.5" /> Temporada Navideña
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900">
            Panel de Bicicletas Navideñas
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestión de inventario y precios para bicicletas infantiles y juveniles (Zombie, Carolina, Sprint).
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchBikes}
            className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 transition-colors"
            title="Recargar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <a
            href="/navidad"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-black text-white text-xs font-bold uppercase rounded-xl transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> Ver en Tienda
          </a>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase rounded-xl shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Agregar Bici
          </button>
        </div>
      </div>

      {/* Alert Notification */}
      {message && (
        <div className={`p-4 rounded-xl text-sm flex items-center justify-between ${
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        }`}>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="p-1 hover:opacity-75">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <span className="text-xs uppercase font-bold text-gray-400">Total Modelos</span>
          <p className="text-2xl font-black text-gray-900 mt-1">{bikes.length}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <span className="text-xs uppercase font-bold text-emerald-600">Disponibles</span>
          <p className="text-2xl font-black text-emerald-600 mt-1">
            {bikes.filter(b => b.disponible).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <span className="text-xs uppercase font-bold text-amber-600">Aros Cubiertos</span>
          <p className="text-sm font-black text-gray-900 mt-1">
            {Array.from(new Set(bikes.map(b => b.categoria)))
              .filter(Boolean)
              .sort((a, b) => Number(a) - Number(b))
              .map(a => `${a}"`)
              .join(', ') || '12", 16", 20", 24", 26", 29"'}
          </p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <span className="text-xs uppercase font-bold text-gray-400">Rango de Precios</span>
          <p className="text-lg font-black text-gray-900 mt-1">
            {bikes.length > 0 ? (
              <>RD$ {Math.min(...bikes.map(b => Number(b.precio) || 0)).toLocaleString()} - {Math.max(...bikes.map(b => Number(b.precio) || 0)).toLocaleString()}</>
            ) : 'RD$ 0'}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Buscar por nombre, código (ej. B398) o aro..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-700">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs font-black uppercase text-gray-500">
              <tr>
                <th className="py-4 px-4">Foto</th>
                <th className="py-4 px-4">Nombre / Modelo</th>
                <th className="py-4 px-4">Aro</th>
                <th className="py-4 px-4">Código</th>
                <th className="py-4 px-4">Precio (DOP)</th>
                <th className="py-4 px-4">Estado</th>
                <th className="py-4 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBikes.map((bike) => {
                const isEditing = editingId === bike.id;
                return (
                  <tr key={bike.id} className="hover:bg-gray-50/80 transition-colors">
                    
                    {/* Thumbnail */}
                    <td className="py-3 px-4">
                      <div className="w-14 h-14 bg-gray-100 rounded-xl overflow-hidden p-1 flex items-center justify-center border border-gray-200">
                        <img
                          src={bike.imagen_url}
                          alt={bike.nombre}
                          className="w-full h-full object-contain"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/fotos-bicicletas/raymon-tourray-1.webp';
                          }}
                        />
                      </div>
                    </td>

                    {/* Nombre & Modelo */}
                    <td className="py-3 px-4">
                      <div className="font-bold text-gray-900">{bike.nombre}</div>
                      <div className="text-xs text-gray-400">{bike.descripcion || bike.modelo}</div>
                    </td>

                    {/* Aro */}
                    <td className="py-3 px-4">
                      <span className="inline-block bg-gray-100 text-gray-800 text-xs font-black px-2.5 py-1 rounded-md">
                        Aro {bike.categoria}"
                      </span>
                    </td>

                    {/* Código */}
                    <td className="py-3 px-4 font-mono text-xs text-gray-500">
                      {bike.codigo || '—'}
                    </td>

                    {/* Precio Editable */}
                    <td className="py-3 px-4">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            className="w-28 px-2 py-1 border border-emerald-500 rounded-lg text-sm font-bold focus:outline-none"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSavePrice(bike.id)}
                            className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                            title="Guardar"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg"
                            title="Cancelar"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-black text-gray-900 text-base">
                            RD$ {Number(bike.precio).toLocaleString()}
                          </span>
                          <button
                            onClick={() => handleStartEdit(bike)}
                            className="p-1 text-gray-400 hover:text-gray-900 transition-colors"
                            title="Editar precio"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Estado Disponible */}
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleToggleDisponible(bike)}
                        className={`px-2.5 py-1 rounded-full text-xs font-black uppercase transition-colors ${
                          bike.disponible
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        {bike.disponible ? 'Disponible' : 'Agotado'}
                      </button>
                    </td>

                    {/* Acciones */}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleStartEdit(bike)}
                          className="text-xs font-bold text-emerald-600 hover:text-emerald-800"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteBike(bike.id, bike.nombre)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                          title="Eliminar bicicleta"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Add Bike */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl relative my-8">
            <button
              onClick={() => setShowAddModal(false)}
              className="absolute top-5 right-5 p-2 text-gray-400 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-black uppercase text-gray-900 mb-4">
              Agregar Bicicleta Navideña
            </h3>

            <form onSubmit={handleCreateBike} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase text-gray-500 block mb-1">Nombre</label>
                <input
                  type="text"
                  placeholder="Ej: Bic. 16 BMX Julen Zombie Verde"
                  value={newBike.nombre}
                  onChange={(e) => setNewBike({ ...newBike, nombre: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500 block mb-1">Aro (Categoría)</label>
                  <select
                    value={newBike.categoria}
                    onChange={(e) => setNewBike({ ...newBike, categoria: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm bg-white"
                  >
                    <option value="12">Aro 12"</option>
                    <option value="16">Aro 16"</option>
                    <option value="20">Aro 20"</option>
                    <option value="24">Aro 24"</option>
                    <option value="26">Aro 26"</option>
                    <option value="29">Aro 29"</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500 block mb-1">Precio (DOP)</label>
                  <input
                    type="number"
                    placeholder="Ej: 7650"
                    value={newBike.precio}
                    onChange={(e) => setNewBike({ ...newBike, precio: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500 block mb-1">Código</label>
                  <input
                    type="text"
                    placeholder="Ej: B399-2"
                    value={newBike.codigo}
                    onChange={(e) => setNewBike({ ...newBike, codigo: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500 block mb-1">Modelo</label>
                  <input
                    type="text"
                    placeholder="Ej: [F2001-16]"
                    value={newBike.modelo}
                    onChange={(e) => setNewBike({ ...newBike, modelo: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase text-gray-500 block mb-1">Descripción / Color</label>
                <input
                  type="text"
                  placeholder="Ej: Verde Neón (Alloy V-Brake)"
                  value={newBike.descripcion}
                  onChange={(e) => setNewBike({ ...newBike, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                />
              </div>

              {/* Subida o URL de Imagen */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-gray-500 block">Foto de la Bicicleta</label>
                
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                  className="hidden" 
                />

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="inline-flex items-center gap-2 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold uppercase rounded-xl transition-colors disabled:opacity-50"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                        <span>Subiendo imagen...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 text-emerald-600" />
                        <span>Subir desde dispositivo</span>
                      </>
                    )}
                  </button>

                  <span className="text-xs text-gray-400">o pega la URL abajo</span>
                </div>

                <input
                  type="url"
                  placeholder="https://... (URL pública de la imagen)"
                  value={newBike.imagen_url}
                  onChange={(e) => setNewBike({ ...newBike, imagen_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                />

                {newBike.imagen_url && (
                  <div className="mt-2 flex items-center gap-3 p-2 bg-gray-50 rounded-xl border border-gray-100">
                    <img 
                      src={newBike.imagen_url} 
                      alt="Vista previa" 
                      className="w-12 h-12 object-contain bg-white rounded-lg border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="text-xs text-gray-500 truncate flex-1">
                      {newBike.imagen_url}
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewBike({ ...newBike, imagen_url: '' })}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="Quitar imagen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="pt-3 flex justify-end gap-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploadingImage}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold uppercase shadow-sm"
                >
                  Guardar Bicicleta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      )}

    </div>
  );
};

export default ChristmasBikesAdmin;
